<template>
  <div class="visitors-milestone-detail page-full-height">
    <div class="page-body">
      <div class="page-wrapper">
        <div class="page-container visitors-milestone-detail__top">
          <PxButton
            v-bind="backToOverviewButton"
            data-testid="back-to-overview-button"
            @click="goToOverviewPage"
          />
          <PxButton
            v-bind="openDialogButtonCopy"
            class="visitors-milestone-detail__button"
            data-testid="onboarding-dialog-trigger"
            @click="openOnboardingDialog"
          />
        </div>
        <div class="page-container visitors-milestone-detail__page-container">
          <PxPanel v-loading="isLoadingView">
            <div class="visitors-milestone-detail__panel">
              <template v-if="!isLoadingView">
                <div
                  v-if="selectedCategory"
                  class="visitors-milestone-detail__navigation-bar"
                >
                  <MilestonePlanNavigationBar
                    ref="milestonesNavigationBar"
                    v-model:active-level="selectedLevel"
                    v-model:current-category="selectedCategory"
                    :categories="gridCategories"
                  />
                </div>
                <div
                  v-if="selectedCategory && selectedGridMilestone"
                  class="visitors-milestone-detail__main"
                >
                  <MilestonePlanHeader
                    :category-color="selectedCategory.color"
                    :category-description="selectedGridMilestone.description"
                    :category-name="selectedCategory.name"
                    :has-evidence="selectedGridMilestone.evidenceProvided"
                    :is-future-milestone="selectedGridMilestone.future"
                    :is-milestone-complete="selectedGridMilestone.completed"
                    :is-milestone-critical="selectedGridMilestone.critical"
                    :is-milestone-in-progress="selectedGridMilestone.inProgress"
                    :is-milestone-planned="selectedGridMilestone.planned"
                    :is-visitor="!$user.isOwner()"
                    :level-number="selectedLevel"
                    :target-date="selectedGridMilestone.targetDate"
                  />
                  <MilestoneForm
                    v-model:active-tab="activeTab"
                    v-model:complete-form-has-changes="completeFormHasChanges"
                    v-model:complete-form-is-invalid="completeFormIsInvalid"
                    v-model:plan-form-has-changes="planFormHasChanges"
                    v-model:plan-form-is-complete="planFormIsComplete"
                    v-model:plan-form-is-invalid="planFormIsInvalid"
                    v-loading="isLoadingForm"
                    :category="selectedCategory"
                    :category-level="selectedCategoryLevel"
                    :complete-last-update="completeLastUpdate"
                    :has-completion-info="
                      selectedGridMilestone.hasCompletionInfo
                    "
                    :is-future-milestone="selectedGridMilestone.future"
                    :is-milestone-complete="selectedGridMilestone.completed"
                    :is-milestone-planned="milestoneHasPlan"
                    :is-plan-published="selectedGridMilestone.planned"
                    :is-visitor="!$user.isOwner()"
                    :milestone="categoryMilestone"
                    :plan-last-update="planLastUpdate"
                    :question-bundles="currentQuestionBundles"
                    data-testid="milestone-form"
                  />
                </div>
              </template>
            </div>
          </PxPanel>
        </div>
      </div>
    </div>
    <MilestonePlanOnboardingDialog
      v-model:visibility="onboardingDialogVisibility"
      :is-visitor="!$user.isOwner()"
      @close-dialog="closeOnboardingDialog"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import moment from "moment";

import MilestonePlanOnboardingDialog from "@/modules/milestone-planner/components/milestone-plan-onboarding-dialog/milestone-plan-onboarding-dialog.vue";
import MilestonePlanHeader from "@/modules/milestone-planner/components/milestone-plan-header/milestone-plan-header.vue";
import MilestoneForm from "@/modules/milestone-planner/components/milestone-form/milestone-form.vue";
import MilestonePlanNavigationBar from "@/modules/milestone-planner/components/milestone-plan-navigation-bar/milestone-plan-navigation-bar.vue";

import {
  IGridMilestone,
  IGridCategory,
} from "@/modules/milestone-planner/components/milestones-grid/milestones-grid.interface";
import { EPxButtonType } from "@/components/px-button/px-button.types";
import {
  ICategory,
  ICategoryDetail,
} from "@/services/data/category/category.interface";
import { ILevel } from "@/services/data/level/level.interface";
import { ECategoryActions } from "@/services/store/category/category-types";
import { ELevelActions } from "@/services/store/levels/levels-types";
import { ROUTE_MILESTONE_PLANNER_OVERVIEW_VISITORS } from "@/modules/milestone-planner/services/router/routes-names";
import {
  IMilestone,
  MILESTONE_DETAILS_TABS,
  TMilestoneDetailsTab,
} from "@/modules/milestone-planner/services/data/milestones/milestone.interface";
import { IQuestionBundle } from "@/services/data/question-bundle/question-bundle.interface";
import { QuestionBundlesState } from "@/modules/milestone-planner/services/store/milestone-planner/sub-modules/question-bundles/question-bundles.module";
import { ENTREPRENEUR_USER_GROUP_ID } from "@/modules/common/constants";
import isFinite from "lodash/isFinite";
import { MILESTONE_PLANNER_VISITOR_PLAN_ONBOARDED } from "@/modules/milestone-planner/constants";
import { MATCHING_LOWEST_LEVEL } from "@/modules/matching/constants";

export default defineComponent({
  name: "VisitorsMilestoneDetailView",

  components: {
    MilestonePlanOnboardingDialog,
    MilestonePlanHeader,
    MilestoneForm,
    MilestonePlanNavigationBar,
  },

  data() {
    return {
      onboardingDialogVisibility: false,
      selectedCategory: null as null | IGridCategory,
      selectedLevel: MATCHING_LOWEST_LEVEL as ILevel["value"],
      planFormIsComplete: false,
      planFormHasChanges: false,
      planFormIsInvalid: false,
      completeFormIsInvalid: false,
      completeFormHasChanges: false,
      activeTab: MILESTONE_DETAILS_TABS.PLAN as TMilestoneDetailsTab,
    };
  },

  static: {
    linkButton: {
      type: EPxButtonType.LINK,
    },
  },

  computed: {
    hasOnboarded(): boolean {
      return this.$store.get("milestonePlanner/onboardings/data")[
        MILESTONE_PLANNER_VISITOR_PLAN_ONBOARDED
      ];
    },

    backToOverviewButton(): any {
      return {
        ...this.$options.static.linkButton,
        label: this.$t(
          "milestonePlanner.milestonePlan.planView.backToOverviewLink",
        ),
      };
    },

    openDialogButtonCopy(): any {
      return {
        ...this.$options.static.linkButton,
        icon: "icon-question",
        label: this.$t("milestonePlanner.milestonePlan.planView.howToUseLink"),
      };
    },

    isLoadingCategories(): boolean {
      return this.$store.get("categories.loading");
    },

    isLoadingLevels(): boolean {
      return this.$store.get("levels.loading");
    },

    isLoadingMilestones(): boolean {
      return this.$store.get("milestonePlanner/loading");
    },

    isLoadingQuestionBundles(): boolean {
      return this.$store.get("milestonePlanner/questionBundles.loading");
    },

    isLoadingView(): boolean {
      return (
        this.isLoadingCategories ||
        this.isLoadingLevels ||
        this.isLoadingMilestones
      );
    },

    isLoadingForm(): boolean {
      return this.isLoadingMilestones || this.isLoadingQuestionBundles;
    },

    categories(): ICategory[] {
      return this.$store.get("categories.data");
    },

    levels(): ILevel[] {
      return this.$store.get("levels.data");
    },

    milestones(): IMilestone[] {
      return this.$store.get("milestonePlanner/value.milestones");
    },

    categoryMilestone(): IMilestone | null {
      return (
        this.milestones.find(
          (milestone) =>
            milestone.category_level.category.id ===
              this.selectedCategory?.id &&
            milestone.category_level.level === this.selectedLevel,
        ) || null
      );
    },

    gridCategories(): IGridCategory[] {
      return this.$store.get("milestonePlanner/parsedValue");
    },

    selectedGridCategory(): IGridCategory | null {
      return (
        this.gridCategories.find(
          (category) => category.id === this.selectedCategory?.id,
        ) || null
      );
    },

    selectedGridMilestone(): IGridMilestone | null {
      const milestones = this.selectedGridCategory?.milestones || [];
      return (
        milestones.find(
          (milestone: IGridMilestone) => milestone.level === this.selectedLevel,
        ) || null
      );
    },

    selectedCategoryLevel(): ICategoryDetail | null {
      const selectedCategory = this.categories.find(
        (category) => category.id === this.selectedCategory?.id,
      );

      if (!selectedCategory) {
        return null;
      }

      return (
        selectedCategory.categoryDetails.find(
          (categoryLevel) => categoryLevel.level.value === this.selectedLevel,
        ) || null
      );
    },

    questionBundles(): Array<IQuestionBundle> {
      return this.$store.get("milestonePlanner/questionBundles.values");
    },

    currentQuestionBundles(): Array<IQuestionBundle> {
      const currentCategory = this.categories.find(
        (category) => category.id === this.selectedCategory?.id,
      );
      const currentCategoryLevel = currentCategory?.categoryDetails.find(
        (categoryDetail) => categoryDetail.level.value === this.selectedLevel,
      );
      const levelId = currentCategoryLevel?.id;

      return this.questionBundles.filter(
        (questionBundle) => questionBundle.category_level === levelId,
      );
    },

    planLastUpdate(): string {
      // TODO: Pending last update from plan on API (for now we'll use milestone global update)
      return this.categoryMilestone && this.milestoneHasPlan
        ? moment(this.categoryMilestone.updated_at).format("MMMM D, YYYY")
        : "";
    },

    completeLastUpdate(): string {
      // TODO: Pending last update from complete on API (for now we'll use milestone global update)
      return this.categoryMilestone
        ? moment(this.categoryMilestone.updated_at).format("MMMM D, YYYY")
        : "";
    },

    milestoneHasPlan(): boolean {
      return (
        !!this.categoryMilestone?.strategy ||
        !!this.categoryMilestone?.outcomes ||
        !!this.categoryMilestone?.resources ||
        !!this.categoryMilestone?.critical ||
        isFinite(this.categoryMilestone?.finances_needed) ||
        !!this.categoryMilestone?.target_date
      );
    },
  },

  watch: {
    "$route.params.categoryId": function (newCategoryId: string) {
      const newCategory = this.gridCategories.find(
        (category) => category.id === Number(newCategoryId),
      );

      if (newCategory) {
        this.selectedCategory = newCategory;
      } else {
        this.$router.replace({
          name: ROUTE_MILESTONE_PLANNER_OVERVIEW_VISITORS,
        });
      }
    },

    "$route.params.levelValue": function (newLevelValue: string) {
      const hasValidLevel = this.isLevelValid(newLevelValue);

      if (hasValidLevel) {
        this.selectedLevel = Number(newLevelValue);
      } else {
        this.$router.replace({
          name: ROUTE_MILESTONE_PLANNER_OVERVIEW_VISITORS,
        });
      }
    },

    "$route.query.tab": function (newTab: TMilestoneDetailsTab) {
      const hasValidTab = this.isTabValid(newTab);

      this.activeTab = hasValidTab ? newTab : MILESTONE_DETAILS_TABS.PLAN;
    },

    activeTab: function (tab: TMilestoneDetailsTab) {
      if (this.$route.query?.tab === tab) {
        return;
      }
      this.$router.push({
        path: this.$route.path,
        query: { tab },
      });
    },

    selectedLevel: function (level: number) {
      if (this.$route.params?.levelValue === level.toString()) {
        return;
      }
      this.$router.push({
        params: { levelValue: level.toString() },
        query: this.$route.query,
      });
    },

    selectedCategory: function (category: IGridCategory) {
      if (this.$route.params?.categoryId === category.id.toString()) {
        return;
      }
      this.$router.push({
        params: { categoryId: category.id.toString() },
        query: this.$route.query,
      });
    },

    selectedCategoryLevel(currentCategoryLevel: ICategoryDetail) {
      if (
        !!currentCategoryLevel &&
        !this.hasAlreadyLoadedQuestionBundle(currentCategoryLevel.id)
      ) {
        this.$store.dispatch(QuestionBundlesState.Action.GET_VALUES, {
          category_level: currentCategoryLevel.id,
        });
      }
    },
  },

  async created() {
    await this.fetchCategoriesAndLevels();

    // Validation relies on having Grid Categories
    this.validateAndSetContext();
  },

  mounted() {
    setTimeout(() => {
      if (!this.hasOnboarded) {
        this.onboardingDialogVisibility = true;
        this.markAsOnboarded();
      }
    }, 2000);
  },

  methods: {
    async fetchCategoriesAndLevels() {
      if (!this.categories.length) {
        await this.$store.dispatch(ECategoryActions.FETCH, {
          group: ENTREPRENEUR_USER_GROUP_ID,
        });
      }

      if (!this.levels.length) {
        await this.$store.dispatch(ELevelActions.FETCH, {
          group: ENTREPRENEUR_USER_GROUP_ID,
        });
      }
    },

    validateAndSetContext() {
      const categoryIdFromRoute = this.$route.params?.categoryId as string;
      const levelValueFromRoute = this.$route.params?.levelValue as string;
      const tabFromRouteQuery = this.$route.query?.tab as TMilestoneDetailsTab;

      const hasValidCategory = this.categories.some(
        (category) => category.id === Number(categoryIdFromRoute),
      );
      const hasValidLevel = this.isLevelValid(levelValueFromRoute);
      const hasValidTab =
        !!tabFromRouteQuery && this.isTabValid(tabFromRouteQuery);

      if (!hasValidCategory || !hasValidLevel) {
        this.$router.replace({
          name: ROUTE_MILESTONE_PLANNER_OVERVIEW_VISITORS,
        });
      } else {
        this.selectedCategory =
          this.gridCategories.find(
            (category) => category.id === Number(categoryIdFromRoute),
          ) || null;
        this.selectedLevel = Number(levelValueFromRoute);
        this.activeTab = hasValidTab
          ? tabFromRouteQuery
          : MILESTONE_DETAILS_TABS.PLAN;
      }
    },

    openOnboardingDialog() {
      this.onboardingDialogVisibility = true;
    },

    closeOnboardingDialog() {
      this.onboardingDialogVisibility = false;
    },

    goToOverviewPage() {
      this.$router.push({ name: ROUTE_MILESTONE_PLANNER_OVERVIEW_VISITORS });
    },

    isTabValid(tab: TMilestoneDetailsTab): boolean {
      return Object.values(MILESTONE_DETAILS_TABS).includes(tab);
    },

    isLevelValid(levelValue: string): boolean {
      return this.levels.some((level) => level.value === Number(levelValue));
    },

    hasAlreadyLoadedQuestionBundle(categoryLevelId: number) {
      return this.questionBundles.some(
        (questionBundle) => questionBundle.category_level === categoryLevelId,
      );
    },

    markAsOnboarded(): void {
      this.$store.commit("milestonePlanner/onboardings/SET_DATA", {
        [MILESTONE_PLANNER_VISITOR_PLAN_ONBOARDED]: true,
      });
    },
  },
});
</script>

<style lang="scss" scoped>
.visitors-milestone-detail {
  max-width: 1140px;
  margin: 0 auto;

  .page-body {
    margin-top: 32px;
  }

  :deep() .px-panel__body {
    padding: 0;
  }

  &__panel {
    display: grid;
    grid-template-columns: 136px auto;
    height: calc(100vh - 41px);
    min-height: 1027px;
  }

  &__main {
    position: relative;
    max-width: 1001px;
  }

  &__top {
    display: grid;
    grid-template-columns: auto auto;
    justify-content: space-between;
    padding: 0 18px 0 16px;
  }

  &__page-container {
    margin-top: 6px;
  }

  &__navigation-bar {
    padding-top: 14px;
    border-right: 1px solid #f0f1f5;
  }

  &__button :deep() span {
    flex-direction: row-reverse;
    gap: 2px;

    img {
      position: relative;
      left: 3px;
    }
  }
}
</style>
<style lang="scss">
.visitors-milestone-detail .milestone-form__header .milestone-plan-state-badge {
  right: 33px;
  bottom: 5px;
}
</style>
